<script setup lang="ts">
import { defineAsyncComponent, computed, ref, inject } from 'vue'
import { isSafeUrl, type UrlSanitizeOptions } from '@/lib/adaptive-card'
import type { Action, Element, ToggleTarget } from '../types'
import { openSafeInNewTab } from '@/lib/urlSafety'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useValidation } from '../composables/useValidation'
import { useCardContext } from '../composables/useCardContext'

const AsyncAdaptiveCard = defineAsyncComponent(() => import('./AdaptiveCard.vue'))

const props = defineProps<{
  actions: Action[]
  onAction?: (action: Action, inputs: Record<string, string>) => void
  elements?: Element[]
}>()

const { validate, inputs } = useValidation(() => props.elements)
const { toggleVisibility } = useCardContext()
const urlOptions = inject<UrlSanitizeOptions>('ac-url-options', {})

const selectedShowCard = ref<number | null>(null)

const selectedShowCardAction = computed(() => {
  if (selectedShowCard.value === null) return null
  const action = props.actions[selectedShowCard.value]
  return action?.type === 'Action.ShowCard' ? action : null
})

function getTooltip(action: Action): string | undefined {
  if (action.type === 'Action.Execute' || action.type === 'Action.OpenUrl' || action.type === 'Action.ToggleVisibility') {
    return action.tooltip
  }
  return undefined
}

function handleClick(action: Action, index: number) {
  if (action.type === 'Action.ShowCard') {
    selectedShowCard.value = selectedShowCard.value === index ? null : index
    return
  }

  if (action.type === 'Action.OpenUrl') {
    if (isSafeUrl(action.url, urlOptions)) {
      openSafeInNewTab(action.url)
    } else {
      console.warn('[AdaptiveCard] Blocked unsafe URL:', action.url)
    }
    return
  }

  if (action.type === 'Action.ToggleVisibility') {
    for (const target of action.targetElements) {
      if (typeof target === 'string') {
        toggleVisibility(target)
      } else {
        toggleVisibility((target as ToggleTarget).elementId, (target as ToggleTarget).isVisible)
      }
    }
    return
  }

  if (!validate()) return
  
  if (props.onAction) {
    props.onAction(action, { ...inputs })
  }
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex flex-wrap gap-2">
      <template v-for="(action, index) in props.actions" :key="index">
        <TooltipProvider v-if="getTooltip(action)">
          <Tooltip>
            <TooltipTrigger as-child>
              <Button
                :variant="selectedShowCard === index ? 'default' : 'outline'"
                @click="handleClick(action, index)"
              >
                {{ action.title ?? (action.type === 'Action.ShowCard' ? 'Show Card' : 'Submit') }}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {{ getTooltip(action) }}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Button
          v-else
          :variant="selectedShowCard === index ? 'default' : 'outline'"
          @click="handleClick(action, index)"
        >
          {{ action.title ?? (action.type === 'Action.ShowCard' ? 'Show Card' : 'Submit') }}
        </Button>
      </template>
    </div>

    <div v-if="selectedShowCardAction" class="border rounded-md p-4">
      <AsyncAdaptiveCard
        :key="selectedShowCard!"
        :template="selectedShowCardAction.card"
        :on-action="props.onAction"
      />
    </div>
  </div>
</template>
